import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building, Building2, Camera, Hammer, HardHat, Home, Info, Layers, Warehouse } from "lucide-react";
import { IMG } from "@/lib/images";
import { Chapter, Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import { usePageMeta } from "@/lib/site";

const FILTERS = [
  { key: "all", label: "All", testid: "project-filter-all" },
  { key: "additions", label: "Additions", testid: "project-filter-additions" },
  { key: "garages", label: "Garages & ADUs", testid: "project-filter-garages" },
  { key: "renovations", label: "Renovations", testid: "project-filter-renovations" },
  { key: "commercial", label: "Commercial", testid: "project-filter-commercial" },
  { key: "concrete", label: "Concrete", testid: "project-filter-concrete" },
];

const SLOTS = [
  { cat: "additions", title: "Home Addition", Icon: Home },
  { cat: "additions", title: "Home Expansion", Icon: HardHat },
  { cat: "garages", title: "Detached Garage", Icon: Warehouse },
  { cat: "garages", title: "Garage Apartment / ADU", Icon: Building2 },
  { cat: "renovations", title: "Home Renovation", Icon: Hammer },
  { cat: "commercial", title: "Commercial Improvement", Icon: Building },
  { cat: "concrete", title: "Concrete Project", Icon: Layers },
  { cat: "renovations", title: "Before & After", Icon: Camera },
];

export default function Projects() {
  usePageMeta(
    "Projects — Utah Construction Portfolio",
    "A portfolio of real Black Sky Builders work across Utah is being prepared — home additions, garages, garage apartments, renovations, concrete, and commercial improvements."
  );
  const [filter, setFilter] = useState("all");
  const visible = SLOTS.filter((s) => filter === "all" || s.cat === filter);

  return (
    <div data-testid="projects-page">
      <PageHero
        eyebrow="Projects — Black Sky Builders"
        lines={["Our Work,", "Documented"]}
        copy="A gallery of real Black Sky Builders projects is being prepared — before-and-after photos, scope, construction challenges, and final results from jobs across Utah."
        image={IMG.hero}
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16">
        <Reveal>
          <div className="flex items-start gap-4 border border-line bg-panel p-5" data-testid="projects-disclaimer">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-silver" />
            <p className="text-sm leading-relaxed text-mist">
              Our portfolio is being prepared. Every project featured on this page will be real, completed Black Sky
              Builders work — photographed, documented, and described honestly. No stock stand-ins, no borrowed photos.
            </p>
          </div>
        </Reveal>

        <div className="mt-12">
          <Chapter index="01" eyebrow="Portfolio" title="Coming soon to this page" />
          <div className="-mt-4 mb-8 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={f.testid}
                onClick={() => setFilter(f.key)}
                aria-pressed={filter === f.key}
                className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  filter === f.key ? "border-white bg-white text-ink" : "border-line text-fog hover:border-steel hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="projects-grid">
            {visible.map((slot, i) => (
              <Reveal key={slot.title} delay={(i % 4) * 0.06}>
                <div className="flex aspect-[4/3] flex-col items-center justify-center border border-dashed border-steel/60 bg-coal p-6 text-center">
                  <slot.Icon className="h-6 w-6 text-steel" />
                  <p className="mt-4 font-display text-sm font-bold uppercase tracking-wide">{slot.title}</p>
                  <p className="mt-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-steel">
                    Project photos &amp; details coming soon
                  </p>
                  <p className="mt-3 border border-line px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-steel">
                    Reserved for real Black Sky Builders work
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal className="mt-16 border border-line bg-coal p-8 sm:p-12">
          <h2 className="font-display text-2xl font-black uppercase tracking-tight sm:text-3xl">
            Have a project we should document next?
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-fog">
            Every project in this gallery will start the same way — a conversation about what you want to build.
          </p>
          <Link to="/contact" data-testid="projects-cta-button" className="btn-primary mt-8">
            Request a Project Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
